// Motor de Roteirização SEMANAL (V2) — um roteiro por DIA ÚTIL, com zonas geográficas compactas
// (k-means determinístico), raio máximo, piso mínimo/dia, DUAS passagens (vencidos → encher) e jornada
// com almoço. Reproduz a especificação Roteirizacao_Regras_Desenvolvedor.md (protótipo do Sandro 977).
// Função PURA e DETERMINÍSTICA (sementes fixas) — roteia UM vendedor. OSRM/tempo real fica p/ etapa
// posterior (aqui: Haversine × fator de desvio). Nasceu ao lado do motor de 1 dia; hoje é o único —
// aquele foi aposentado em 29/08/2026 e este é o que a tela do Roteiro da Semana usa.
import { Client, RoteirizacaoSettings } from '../types';
import { hhmmToMin, minToHHMM, haversineKm, cicloDe, LatLng, SmartStop } from './smartRoute';
import { ehZonaRodizio, ehMunicipioSaoPaulo as ehMunicipioSP, diaRodizioDaPlaca } from './rodizioSp';

export interface DiaRoteiro {
  indice: number;                 // 0..ndays-1
  dia: string;                    // rótulo (Segunda-feira, ...)
  rota: SmartStop[];
  almoco: { inicio: string; fim: string } | null;
  encerramento: string | null;    // saída da última visita
  kmAprox: number;
}
export interface DistanteInfo { clientId: string; nome: string; municipio: string; estrelas: number; distKm: number; }
export interface BacklogInfo { clientId: string; nome: string; municipio: string; estrelas: number; diasAtraso: number; }
// Cliente que o vendedor REMARCOU para um dia e que nem assim coube. Existe para nada sumir em
// silêncio: um cliente mandado à mão para a terça não pode simplesmente desaparecer da semana.
// Acontece quando só a ida e a volta já estouram a jornada (cliente absurdamente distante da partida).
export interface FixadoInfo { clientId: string; nome: string; municipio: string; dia: number; }
// Cliente do Centro Expandido que o rodízio deixou de fora, com o MOTIVO. Os dois motivos pedem ações
// diferentes: 'rodizio' é a regra funcionando (reagendar noutro dia); 'coordenada_imprecisa' é cadastro
// ruim (o cliente pode nem estar na zona) e o conserto é geocodificar, não remarcar.
export interface RodizioInfo { clientId: string; nome: string; municipio: string; motivo: 'rodizio' | 'coordenada_imprecisa'; }

export interface WeeklyRouteResult {
  data: string;
  dias: DiaRoteiro[];
  distantes: DistanteInfo[];      // além do raio máximo → viagens dedicadas
  backlog: BacklogInfo[];         // vencidos que não couberam
  alertas: { semCoordenada: number };
  resumo: { roteirizaveis: number; porEstrela: { 1: number; 2: number; 3: number }; visitas: number; kmAprox: number };
  // Rodízio de SP. `aplicado` distingue "a regra rodou e não tirou ninguém" de "a regra nem rodou"
  // (desligada, sem placa, ou o dia restrito cai fora da semana planejada) — sem isso a tela não teria
  // como explicar a ausência do aviso.
  // `troca` tem QUATRO estados porque um booleano confundia dois opostos: "não precisou trocar" e
  // "não HAVIA para onde trocar, então o dia restrito foi esvaziado". A tela dizia a mesma frase
  // tranquilizadora nos dois, justamente no pior deles.
  rodizio: {
    aplicado: boolean;
    diaRestrito: number | null;
    troca: 'nao_aplicado' | 'nao_precisou' | 'trocada' | 'sem_zona_livre';
    aRever: RodizioInfo[];
  };
  // Remarcações que NÃO couberam no dia escolhido (ver FixadoInfo). Vazio no caso normal.
  fixadosNaoCouberam: FixadoInfo[];
}

export interface WeeklyOpts {
  dataRef: Date;
  pontoPartida: LatLng;
  ndays?: number;        // padrão 5 (dias úteis)
  maxRadiusKm?: number;  // padrão 50
  minVisitas?: number;   // padrão 4
  alpha?: number;        // padrão 8 (proximidade × prioridade)
  detour?: number;       // padrão 1.4 (fator de desvio da linha reta)
  flaggedLater?: Iterable<string>; // "visitar depois": clientIds forçados a entrar (como vencidos/prioritários)
  // RODÍZIO DE SP: dia da semana restrito pela placa (1=segunda … 5=sexta), ou null/ausente para não
  // aplicar. Quem converte placa→dia é a TELA (`utils/rodizioSp`), não o motor: assim o motor continua
  // uma função pura de roteirização, sem saber o que é placa.
  diaRodizio?: number | null;
  // REMARCAÇÃO MANUAL: clientId → índice do dia (0 = segunda). O vendedor mandou este cliente para
  // este dia, e o motor obedece — é ordem, não sugestão. Diferente de `flaggedLater`, que só força o
  // cliente a ENTRAR na semana e deixa a geografia escolher o dia.
  // Quem traduz a marca guardada (com o carimbo da semana) neste mapa é a TELA, não o motor — mesmo
  // princípio do `diaRodizio`: o motor não sabe o que é semana nem o que é `roteiroFlags`.
  fixados?: Record<string, number>;
}

// ---------------------------------------------------------------------------------------------
// ENTRADAS DA GERAÇÃO — compartilhadas entre o APP e o PAINEL
// ---------------------------------------------------------------------------------------------
// Estas funções moravam na tela (`RoteiroSemanalScreen`). Vieram para cá quando o painel web ganhou a
// aba "Rotas", que precisa reproduzir a MESMA rota: o motor já era determinístico, mas quem montava as
// entradas era a tela — e duas telas montando `opts` à mão divergiriam em silêncio, que é exatamente o
// defeito que a aba nova existe para não ter. Este arquivo é copiado byte a byte para `admin/src/utils`
// (o teste `motorEmSincronia` falha se as cópias divergirem), então os dois lados chamam este código.

/** Raio máximo, em km, medido do ponto de partida.
 *
 *  É o corte que descartava os 964 clientes do supervisor 915 (carteira em SC/RS, gestor no ES). Vive
 *  aqui, e não na tela, porque o painel precisa do mesmo número — e porque o alerta de "0 visitas" cita
 *  o valor, que sairia de sincronia se cada lado tivesse o seu. */
export const RAIO_MAX_KM = 50;

/** Ordena a carteira por `id`, de forma estável.
 *
 *  ⚠️ NÃO é cosmético — é o que torna a rota reproduzível. O k-means++ escolhe o primeiro centróide por
 *  ÍNDICE (`pts[Math.floor(rnd() * pts.length)]`) e o desempate em `agendar` é `val > bestVal`, então o
 *  primeiro da lista vence. A carteira chega de uma mesclagem de queries do Firestore num `Map`, cuja
 *  ordem não é garantida: sem esta ordenação, o MESMO vendedor podia gerar duas rotas diferentes sem
 *  nada ter mudado, e o painel não teria como reproduzir a rota do app nem com todas as outras entradas
 *  iguais. Copia antes de ordenar: `sort` é in-place e o array vem de um `useMemo` de quem chama. */
export function ordenarCarteira(clients: Client[]): Client[] {
  return clients.slice().sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
}

/** Zera a hora: a data de referência passa a ser a MEIA-NOITE LOCAL do dia.
 *
 *  `diasAtraso` sai de `floor((dataRef - lastCheckinAt) / 1 dia)`, então com a hora do toque a mesma
 *  carteira produzia rotas diferentes ao longo do dia, conforme cada cliente cruzava a fronteira de
 *  24 h. Congelar no dia deixa a geração idempotente enquanto o dia durar — e é o que permite ao painel
 *  simular à tarde a rota que o vendedor gerou de manhã. */
export function normalizarDataRef(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

/** Instante da meia-noite LOCAL do dia de `d`. Usado dos dois lados da conta de `diasDesde` — zerar só
 *  um lado desequilibra a subtração e some com um dia de atraso da carteira inteira. */
const meiaNoiteLocal = (d: Date): number => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();

/** Segunda-feira da semana que `d` PLANEJA, como 'YYYY-MM-DD'. É a chave do roteiro salvo
 *  (`{uid}_{semana}`), e o painel procura por ela.
 *
 *  Dia útil devolve a segunda daquela semana. ⚠️ SÁBADO E DOMINGO ROLAM PARA A SEGUINTE, e isso não é
 *  detalhe: o motor sempre monta um plano de segunda a sexta, então quem gera no sábado está planejando
 *  a semana que vem. Mandando o fim de semana para a segunda ANTERIOR (que foi a primeira versão), o
 *  `setDoc` com merge:false SUBSTITUÍA o roteiro da semana que acabou de terminar — destruindo o
 *  histórico daquela semana — e o plano novo ficava invisível para o gestor na segunda, porque ele
 *  procura pela chave da semana corrente. */
export function inicioDaSemana(d: Date): string {
  const x = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const dow = x.getDay(); // 0=domingo … 6=sábado
  if (dow === 6) x.setDate(x.getDate() + 2);        // sábado  -> segunda seguinte
  else if (dow === 0) x.setDate(x.getDate() + 1);   // domingo -> segunda seguinte
  else x.setDate(x.getDate() - (dow - 1));          // seg..sex -> segunda desta semana
  const mm = String(x.getMonth() + 1).padStart(2, '0');
  const dd = String(x.getDate()).padStart(2, '0');
  return `${x.getFullYear()}-${mm}-${dd}`;
}

/** Data de calendário ('YYYY-MM-DD') do dia `indice` (0 = segunda) da semana de `dataRef`.
 *
 *  É o que carimba `routes.dataPlanejada` — sem ela o Meu Dia não tem como saber PARA QUE DIA a rota
 *  foi feita, e acaba mostrando a de ontem para sempre, que era o comportamento anterior. */
export function dataDoDia(dataRef: Date, indice: number): string {
  const seg = inicioDaSemana(dataRef).split('-').map(Number);
  const d = new Date(seg[0], seg[1] - 1, seg[2] + Math.max(0, indice));
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mm}-${dd}`;
}

/** Data de HOJE no mesmo formato de `dataDoDia`, para comparar sem fuso nem Timestamp. */
export function hojeISO(agora: Date): string {
  const mm = String(agora.getMonth() + 1).padStart(2, '0');
  const dd = String(agora.getDate()).padStart(2, '0');
  return `${agora.getFullYear()}-${mm}-${dd}`;
}

export interface EntradasDaGeracao {
  dataRef: Date;          // normalizada aqui dentro; pode vir `new Date()` cru
  origin: LatLng;         // GPS no app; ponto clicado no mapa, no painel
  flagged?: Iterable<string>; // "visitar depois" (roteiroFlags/{sellerId})
  placa?: string | null;  // users/{uid}.placaVeiculo — só importa se o rodízio estiver ligado
  fixados?: Record<string, number>; // remarcações à mão, já resolvidas para índice de dia
}

/** Monta o `WeeklyOpts` a partir dos parâmetros do painel (`settings/roteirizacao`).
 *
 *  ⚠️ O SANEAMENTO É AQUI, e não dentro do motor: o `??` de lá só cobre null/undefined, então um `0`
 *  gravado no doc PASSARIA — e `detour: 0` zera toda distância de deslocamento, fazendo o motor achar
 *  que cabe um número infinito de visitas por dia. Um campo esvaziado na tela web vira `Number('')`,
 *  que é 0, então esse valor chega mesmo.
 *  ⚠️ TETO, e não só piso. `ndays` vira o K do k-means E o número de voltas do laço que monta os dias:
 *  um `diasRota: 400` digitado por engano (o `max` do input HTML não impede a gravação) agruparia 400
 *  centróides e renderizaria 400 dias — congela o aparelho do vendedor em campo.
 *  Fora da faixa volta ao PADRÃO em vez de truncar: um valor absurdo é erro de digitação, e virar 7 em
 *  silêncio esconderia o engano do gestor, que continuaria vendo "400" na tela de configuração. */
export function montarOpts(settings: RoteirizacaoSettings, e: EntradasDaGeracao): WeeklyOpts {
  const num = (v: unknown, padrao: number, min: number, max: number) =>
    (typeof v === 'number' && Number.isFinite(v) && v >= min && v <= max ? v : padrao);
  return {
    dataRef: normalizarDataRef(e.dataRef),
    pontoPartida: e.origin,
    flaggedLater: e.flagged,
    maxRadiusKm: RAIO_MAX_KM,
    detour: num(settings.fatorDesvio, 1.4, 1, 3),      // acima de 3 não é desvio, é erro
    // Arredondado: o motor faz `for (d = 0; d < ndays; d++)` e usa ndays como K do k-means — um 5,5
    // vindo do painel geraria SEIS dias (um a mais que o configurado) e um K fracionário.
    ndays: Math.round(num(settings.diasRota, 5, 1, 7)),
    minVisitas: num(settings.minVisitasDia, 4, 0, 30), // 30 visitas/dia já é irreal
    // Desligado no painel ou sem placa ⇒ null ⇒ o motor nem calcula ponto-em-polígono.
    diaRodizio: settings.rodizioSpAtivo ? diaRodizioDaPlaca(e.placa) : null,
    fixados: e.fixados || {},
  };
}

/** Índice de dia (0 = segunda) das marcas de remarcação válidas para a semana de `dataRef`.
 *
 *  A marca guarda `{ dia, semana }` e a de OUTRA semana é simplesmente ignorada — é assim que ela
 *  expira sozinha, sem expurgo. "O cliente pediu para amanhã" é um evento, não uma regra permanente:
 *  sem esse corte, meses depois ninguém entenderia por que aquele cliente cai sempre na terça. */
export function fixadosDaSemana(
  marcas: Record<string, { dia: number; semana: string }> | null | undefined,
  dataRef: Date
): Record<string, number> {
  const semana = inicioDaSemana(dataRef);
  const out: Record<string, number> = {};
  for (const [clientId, m] of Object.entries(marcas || {})) {
    if (!m || m.semana !== semana) continue;
    if (typeof m.dia !== 'number' || !Number.isInteger(m.dia) || m.dia < 0 || m.dia > 6) continue;
    out[clientId] = m.dia;
  }
  return out;
}

const DIAS_LABEL = ['Segunda-feira', 'Terça-feira', 'Quarta-feira', 'Quinta-feira', 'Sexta-feira', 'Sábado', 'Domingo'];

// RNG determinístico (mulberry32): sementes fixas garantem rota estável a cada geração.
function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6D2B79F5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

interface WCand {
  c: Client;
  pos: LatLng;
  estrelas: number;
  ciclo: number;
  diasDesde: number | null;
  diasAtraso: number;    // >0 vencido; nunca visitado = ciclo (vencido há um intervalo inteiro)
  pscore: number;        // vencidos: estrelas*1000 + max(0, atraso)
  fillscore: number;     // não vencidos: estrelas*1000 + atraso (pode ser negativo)
  elegivel: boolean;     // vence nesta semana (atraso >= -janela)
  municipio: string;
  bairro: string;
  zona: number;
  // RODÍZIO: `emRodizio` = dentro do Centro Expandido, com coordenada em que dá para confiar.
  // `coordImprecisa` = está em São Paulo com `geoPrecision: 'city'` (a coordenada é o centro do
  // município, típico de CEP genérico). Este NÃO é excluído da rota — a coordenada não prova que ele
  // está na zona, e tirar visita por cadastro ruim é pior que o risco. Vai só para a lista "a rever".
  emRodizio: boolean;
  coordImprecisa: boolean;
}

// ---------- k-means (compacidade, sementes fixas, menor raio médio) ----------
//
// ⚠️ REESCRITO POR PERFORMANCE em 18/09/2026 — a ROTA NÃO MUDOU. Quem garante isso é
// `smartWeekRoute.exatidao.test.ts`, que compara 12 cenários com o `fixtures/rotaGolden.json` gravado
// ANTES desta mudança. Se aquele teste ficar vermelho depois de um ajuste aqui, o ajuste está errado.
//
// O PROBLEMA, medido: o k-means responde por ~88-90% do custo da geração. Com os ~1.600 clientes que
// cabem num raio de 50 km da Grande Vitória (número tirado da produção: 16.057 ativos, 236 vendedores,
// carteira individual com mediana 28 e máximo 521 — o `n` grande é o do GESTOR, que soma a equipe) são
// ~656 mil comparações de distância por geração.
//
// ⚠️ ORDEM DE GRANDEZA, para ninguém repetir o erro que eu cometi: no V8 do desktop a geração inteira
// custava 74-94 ms, não segundos. A primeira medição deu 3,3 s porque rodava dentro do jest, que
// executa em contexto `vm` e inflou TUDO em ~35× — o mesmo código em Node puro deu 94 ms. Se for medir
// este motor de novo, meça FORA do jest.
// O Hermes do aparelho não foi medido (não há como daqui) e é um interpretador sem JIT, então lá o
// número absoluto é bem maior. O que se sustenta nos dois ambientes é a RAZÃO: 2,4x sob o jest e 2,1x
// em Node puro — ambientes com 35x de diferença de velocidade e praticamente o mesmo ganho relativo.
//
// AS QUATRO MUDANÇAS, e por que nenhuma altera o resultado:
//  1. `cos(lat)` de cada ponto sai UMA vez (`comCos`) em vez de duas por comparação. A conta é a mesma
//     do `haversineKm`: `Math.cos((lat * Math.PI) / 180)`.
//  2. Decidir QUEM ESTÁ MAIS PERTO não precisa de km. `havS` devolve o `s` da fórmula de haversine, que
//     cresce junto com a distância — o mesmo argmin, sem `asin`, `sqrt` e `×2R`.
//  3. `asin`/`sqrt` sobraram só onde o VALOR em km importa: o peso do k-means++ e o `raioMedio`. E no
//     k-means++ agora é 1 conversão por ponto (depois do mínimo), não uma por centróide.
//  4. Os centróides deixaram de custar K varreduras com `filter` por iteração e viraram UMA passada
//     acumulando somas. A ordem dos índices é a mesma do `filter`, então a soma em ponto flutuante
//     dá o mesmo número — não é "praticamente igual", é o mesmo.
//
// O que NÃO foi feito, de propósito: nada que mude a rota. Nem reduzir as 5 sementes, nem aproximação
// equirretangular, nem poda por desigualdade triangular (essa depende de aritmética exata para provar
// que a comparação podada não ganharia, e em ponto flutuante isso não se sustenta).

// Raio da Terra, em km. Tem de ser o MESMO que `smartRoute.haversineKm` usa.
const R_KM = 6371;
const paraRad = (d: number) => (d * Math.PI) / 180;

// Os pontos em três `Float64Array` paralelos, em vez de um array de objetos.
// ⚠️ Isto NÃO é o que traz o ganho: medido em isolado, buffer de doubles empatou com array de objetos
// no V8 (0,93x — ou seja, nem isso). Está aqui porque troca 1.600 objetos de heap por 3 buffers a cada
// geração, e o aparelho do vendedor é um Android de 2-3 GB onde alocação importa. Os NÚMEROS são
// idênticos: `Float64Array` guarda um double sem conversão nenhuma.
//
// ⚠️ As coordenadas continuam em GRAUS. O delta do haversine é `toRad(b.lat - a.lat)`, e NÃO
// `toRad(b.lat) - toRad(a.lat)`: em ponto flutuante os dois diferem, e é a primeira forma que o motor
// sempre usou. Guardar os pontos já em radianos pareceria uma economia e mudaria a rota.
interface PontosK { lat: Float64Array; lng: Float64Array; cos: Float64Array; n: number; }
function prepararPontos(pts: LatLng[]): PontosK {
  const n = pts.length;
  const lat = new Float64Array(n); const lng = new Float64Array(n); const cos = new Float64Array(n);
  for (let i = 0; i < n; i++) { lat[i] = pts[i].lat; lng[i] = pts[i].lng; cos[i] = Math.cos(paraRad(pts[i].lat)); }
  return { lat, lng, cos, n };
}

// O `s` do haversine: sin²(Δlat/2) + cos·cos·sin²(Δlng/2), sempre em [0,1]. É monotônico na distância,
// então ordena igual — serve para COMPARAR, não para exibir. Recebe as componentes soltas para não
// obrigar o chamador a montar um objeto por comparação.
function havS(laA: number, lnA: number, coA: number, laB: number, lnB: number, coB: number): number {
  const dLat = paraRad(laB - laA);
  const dLng = paraRad(lnB - lnA);
  return Math.sin(dLat / 2) ** 2 + coA * coB * Math.sin(dLng / 2) ** 2;
}
// Fecho idêntico ao de `haversineKm`: é aqui que o `s` vira quilômetro.
const sParaKm = (s: number) => 2 * R_KM * Math.asin(Math.sqrt(s));

function kmeansRun(P: PontosK, K: number, seed: number): { labels: number[]; centroids: LatLng[] } {
  const rnd = mulberry32(seed);
  const n = P.n;
  const cLat = new Float64Array(K); const cLng = new Float64Array(K); const cCos = new Float64Array(K);
  let nc = 0;
  // Centróide nasce SOBRE um ponto, então o `cos(lat)` dele já está calculado — é o mesmo número.
  const adotarPonto = (i: number) => { cLat[nc] = P.lat[i]; cLng[nc] = P.lng[i]; cCos[nc] = P.cos[i]; nc++; };

  // k-means++ init. Aqui o VALOR importa (é peso de amostragem), mas só depois do mínimo: acha-se o
  // centróide mais próximo comparando `s` e converte-se UMA vez por ponto. `min(d²) = (min d)²` porque
  // distância nunca é negativa, então o número é o mesmo que o `Math.min` sobre os quadrados dava.
  adotarPonto(Math.floor(rnd() * n));
  const d2 = new Float64Array(n);
  const minS = new Float64Array(n).fill(Infinity);
  let jaMedidos = 0; // centróides já incorporados ao mínimo corrente
  while (nc < K) {
    // MÍNIMO INCREMENTAL: `min` é associativo, então basta comparar com os centróides que entraram
    // depois da última rodada. Antes, cada centróide novo refazia a conta contra TODOS os anteriores —
    // O(n·K²/2) em vez de O(n·K). Mesmo mínimo, porque min(min(a,b),c) = min(a,b,c) e min é exato.
    for (let k = jaMedidos; k < nc; k++) {
      const la = cLat[k]; const ln = cLng[k]; const co = cCos[k];
      for (let i = 0; i < n; i++) { const s = havS(P.lat[i], P.lng[i], P.cos[i], la, ln, co); if (s < minS[i]) minS[i] = s; }
    }
    jaMedidos = nc;
    let soma = 0;
    for (let i = 0; i < n; i++) { const v = sParaKm(minS[i]) ** 2; d2[i] = v; soma += v; } // mesma ordem do `reduce(..., 0)`
    if (!soma) soma = 1;  // era o `|| 1`: soma zero (ou NaN) não pode zerar a amostragem
    let alvo = rnd() * soma;
    let idx = 0;
    for (let i = 0; i < n; i++) { alvo -= d2[i]; if (alvo <= 0) { idx = i; break; } }
    adotarPonto(idx);
  }

  // `labels` fica array comum (e não Int32Array) porque sai daqui para fora do módulo; com inteiros
  // pequenos os dois motores guardam isto na forma compacta mesmo assim.
  const labels = new Array<number>(n).fill(0);
  const somaLat = new Float64Array(K); const somaLng = new Float64Array(K); const qtd = new Int32Array(K);
  for (let it = 0; it < 25; it++) {
    let mudou = false;
    for (let i = 0; i < n; i++) {
      const la = P.lat[i]; const ln = P.lng[i]; const co = P.cos[i];
      let melhor = 0; let melhorS = Infinity;
      for (let k = 0; k < K; k++) { const s = havS(la, ln, co, cLat[k], cLng[k], cCos[k]); if (s < melhorS) { melhorS = s; melhor = k; } }
      if (labels[i] !== melhor) { labels[i] = melhor; mudou = true; }
    }
    // Centróides numa passada só. Antes eram K `filter` + 2K `reduce` por iteração (K varreduras
    // completas do array, mais K arrays temporários). Acumular por índice crescente reproduz
    // exatamente a ordem que o `filter` produzia, e com isso a mesma soma em ponto flutuante.
    somaLat.fill(0); somaLng.fill(0); qtd.fill(0);
    for (let i = 0; i < n; i++) { const k = labels[i]; somaLat[k] += P.lat[i]; somaLng[k] += P.lng[i]; qtd[k]++; }
    // Cluster que ficou vazio MANTÉM o centróide anterior — era o que o `if (membros.length)` fazia.
    for (let k = 0; k < K; k++) {
      if (!qtd[k]) continue;
      cLat[k] = somaLat[k] / qtd[k];
      cLng[k] = somaLng[k] / qtd[k];
      cCos[k] = Math.cos(paraRad(cLat[k]));
    }
    if (!mudou) break;
  }
  const centroids: LatLng[] = new Array(K);
  for (let k = 0; k < K; k++) centroids[k] = { lat: cLat[k], lng: cLng[k] };
  return { labels, centroids };
}
function raioMedio(pts: LatLng[], labels: number[], centroids: LatLng[]): number {
  let soma = 0;
  for (let i = 0; i < pts.length; i++) soma += haversineKm(pts[i], centroids[labels[i]]);
  return pts.length ? soma / pts.length : 0;
}
function kmeansZonas(pts: LatLng[], K: number): { labels: number[]; centroids: LatLng[] } {
  if (K <= 1) return { labels: pts.map(() => 0), centroids: [pts[0] || { lat: 0, lng: 0 }] };
  const P = prepararPontos(pts); // o `cos(lat)` serve às 5 sementes; antes cada comparação recalculava
  const seeds = [1, 7, 13, 29, 101]; // várias sementes fixas → escolhe a mais compacta
  let best: { labels: number[]; centroids: LatLng[] } | null = null;
  let bestScore = Infinity;
  for (const s of seeds) {
    const r = kmeansRun(P, K, s);
    const sc = raioMedio(pts, r.labels, r.centroids);
    if (sc < bestScore) { bestScore = sc; best = r; }
  }
  return best!;
}

// ---------- montagem de um dia (2 passagens + piso mínimo) ----------
interface DiaParams {
  inicio: number; fim: number; almIni: number; almFim: number; almocoMin: number;
  dur: number; minVisitas: number; alpha: number; pontoPartida: LatLng;
  travelMin: (a: LatLng, b: LatLng) => number;
}
function montarDia(indice: number, daZona: WCand[], todos: WCand[], usados: Set<string>, P: DiaParams, fixadosDoDia: WCand[] = []): DiaRoteiro {
  const rota: SmartStop[] = [];
  let cur = P.pontoPartida;
  let t = P.inicio;
  let km = 0;
  let almoco: { inicio: string; fim: string } | null = null;

  const tomarAlmoco = () => {
    // Spec §7: almoça ao cruzar 12:00 (relógio >= almIni). SEM teto superior — senão uma perna longa que
    // saltasse a janela [almIni, almFim) "pularia" o almoço e o dia sairia sem parada de almoço.
    if (!almoco && t >= P.almIni) {
      almoco = { inicio: minToHHMM(t), fim: minToHHMM(t + P.almocoMin) };
      t += P.almocoMin;
    }
  };
  // Reserva do almoço no orçamento até ele ser EFETIVAMENTE tirado (não deixa o dia estourar sem almoçar).
  const reserva = () => (!almoco ? P.almocoMin : 0);
  // VOLTA PARA A PARTIDA, memoizada por cliente. Ela não depende de onde o vendedor está (`cur`) nem do
  // relógio (`t`) — só do candidato —, mas era recalculada para CADA candidato a CADA parada colocada:
  // num dia com 13 paradas e 300 candidatos, 3.900 haversines para obter 300 valores distintos. O mapa
  // vive no dia inteiro, então as quatro passagens (remarcados, vencidos, preenchimento, piso) o dividem.
  const voltaCache = new Map<string, number>();
  const voltaMin = (cand: WCand) => {
    let v = voltaCache.get(cand.c.id);
    if (v === undefined) { v = P.travelMin(cand.pos, P.pontoPartida); voltaCache.set(cand.c.id, v); }
    return v;
  };
  const cabe = (tv: number, cand: WCand) => t + tv + P.dur + voltaMin(cand) + reserva() <= P.fim;

  const agendar = (pool: WCand[], scoreFn: (c: WCand) => number, motivoFn: (c: WCand) => SmartStop['motivo'], limite = Infinity) => {
    const rest = pool.filter((x) => !usados.has(x.c.id));
    while (rest.length && rota.length < limite) {
      tomarAlmoco();
      // melhor por valor combinado (prioridade − alpha·deslocamento), só entre os que cabem
      let best: WCand | null = null; let bestVal = -Infinity; let bestTv = 0;
      for (const cand of rest) {
        const tv = P.travelMin(cur, cand.pos);
        if (!cabe(tv, cand)) continue;
        const val = scoreFn(cand) - P.alpha * tv;
        if (val > bestVal) { bestVal = val; best = cand; bestTv = tv; }
      }
      if (!best) break; // nenhum candidato cabe mais no dia
      const chegada = t + bestTv;
      const saida = chegada + P.dur;
      km += haversineKm(cur, best.pos);
      rota.push({
        ordem: rota.length + 1,
        clientId: best.c.id,
        codparc: best.c.sankhya?.codparc ?? null,
        nome: best.c.name || best.c.tradeName || '',
        municipio: best.municipio,
        bairro: best.bairro,
        lat: best.pos.lat,
        lng: best.pos.lng,
        chegada: minToHHMM(chegada),
        saida: minToHHMM(saida),
        duracaoMin: P.dur,
        deslocamentoMin: Math.round(bestTv),
        rede: false,
        estrelas: best.estrelas,
        diasAtraso: Math.max(0, best.diasAtraso),
        motivo: motivoFn(best),
      });
      usados.add(best.c.id);
      cur = best.pos; t = saida;
      const i = rest.indexOf(best); if (i >= 0) rest.splice(i, 1);
    }
  };

  // Passagem ZERO — REMARCADOS À MÃO, antes de qualquer outro.
  // ⚠️ É esta ordem que implementa "entra e empurra o último" SEM nenhuma lógica de remoção. Agendados
  // no dia ainda vazio, eles sempre cabem (a menos que a ida e a volta sozinhas estourem a jornada), e
  // quem deixa de caber no fim é o cliente marginal que o motor escolheria por último — que cai sozinho
  // no `backlog`. Tirar uma parada de um dia já montado deixaria buraco no horário, e foi por isso que
  // o bloqueio do rodízio também nasceu ANTES da montagem, não como filtro depois.
  // Entre si os remarcados são agendados por proximidade (score 0 = só o termo de deslocamento conta),
  // para o dia não virar zigue-zague quando houver vários.
  agendar(fixadosDoDia, () => 0, () => 'remarcado');
  // Passagem 1 — vencidos/elegíveis (obrigatório), por pscore
  agendar(daZona.filter((c) => c.elegivel), (c) => c.pscore, (c) => (c.diasAtraso >= 0 ? 'vencido' : 'vencendo_no_caminho'));
  // Passagem 2 — encher com os que ainda não vencem, por fillscore (antecipada)
  agendar(daZona.filter((c) => !c.elegivel), (c) => c.fillscore, () => 'vencendo_no_caminho');
  // Piso mínimo — completa APENAS até atingir o mínimo (não enche o dia) com os mais próximos de OUTRAS zonas
  if (rota.length < P.minVisitas) {
    agendar(todos.filter((c) => c.zona !== indice), () => 0, () => 'preenchimento', P.minVisitas);
  }

  return {
    indice,
    dia: DIAS_LABEL[indice] || `Dia ${indice + 1}`,
    rota,
    almoco,
    encerramento: rota.length ? rota[rota.length - 1].saida : null,
    kmAprox: Math.round(km),
  };
}

// ---------- entrada principal ----------
export function generateWeeklyRoute(clients: Client[], settings: RoteirizacaoSettings, opts: WeeklyOpts): WeeklyRouteResult {
  const ndays = Math.max(1, opts.ndays ?? 5);
  const maxRadiusKm = opts.maxRadiusKm ?? 50;
  const minVisitas = opts.minVisitas ?? 4;
  const alpha = opts.alpha ?? 8;
  const detour = opts.detour ?? 1.4;
  // 1..5 (segunda..sexta) ou null. Fora dessa faixa é tratado como "sem rodízio": um valor inválido
  // não pode virar índice de zona nem sumir com dia nenhum.
  // ⚠️ `Number.isInteger` não é decoração: só a faixa não basta. Um 1.5 passava, virava
  // `zonaRestrita = 0.5`, atravessava a troca e o bloqueio sem efeito nenhum (0.5 nunca é igual a
  // `c.zona` nem a `d`) e explodia lá embaixo em `dias[0.5].rota` — TypeError. Hoje o único produtor
  // é `diaRodizioDaPlaca`, que devolve inteiro, mas a guarda existe justamente para não depender
  // disso; o driver do motor alcançou o caso que a revisão tinha julgado inalcançável.
  const dr = opts.diaRodizio;
  const diaRodizio = typeof dr === 'number' && Number.isInteger(dr) && dr >= 1 && dr <= 5 ? dr : null;
  const { dataRef, pontoPartida } = opts;
  const flagged = new Set(opts.flaggedLater || []); // "visitar depois" (vazio = comportamento idêntico ao de hoje)
  const janela = settings.janelaToleranciaDias;
  const vKmh = settings.velocidadeMediaKmh > 0 ? settings.velocidadeMediaKmh : 35;
  const travelMin = (a: LatLng, b: LatLng) => (haversineKm(a, b) * detour / vKmh) * 60;

  // 1) universo (ativo + estrelas>=1 + com coordenada) e 2) raio máximo
  let semCoordenada = 0;
  const porEstrela = { 1: 0, 2: 0, 3: 0 } as { 1: number; 2: number; 3: number };
  const cands: WCand[] = [];
  const distantes: DistanteInfo[] = [];

  for (const c of clients) {
    if (c.active === false) continue;
    const estrelasReal = Number(c.estrelas || 0);
    if (estrelasReal < 1 && !flagged.has(c.id)) continue; // §3: só entra estrelas >= 1 (flaggado força a entrada)
    const lat = c.location?.latitude; const lng = c.location?.longitude;
    if (typeof lat !== 'number' || typeof lng !== 'number') { semCoordenada++; continue; } // sem coordenada nunca é roteável, nem flaggado
    const estrelas = flagged.has(c.id) ? Math.max(estrelasReal, 1) : estrelasReal; // flaggado sem estrela usa piso 1
    const pos: LatLng = { lat, lng };
    const municipio = c.address?.city || '';
    const bairro = c.address?.neighborhood || '';
    const estKey = (estrelas >= 3 ? 3 : estrelas) as 1 | 2 | 3;
    porEstrela[estKey]++;

    const distBase = haversineKm(pontoPartida, pos);
    if (distBase > maxRadiusKm && !flagged.has(c.id)) { // flaggado ignora o raio máximo (escolha manual)
      distantes.push({ clientId: c.id, nome: c.name || c.tradeName || '', municipio, estrelas, distKm: Math.round(distBase) });
      continue;
    }

    const ciclo = cicloDe(estrelas, settings) as number; // estrelas>=1 → nunca null
    const ultima = c.lastCheckinAt ? new Date(c.lastCheckinAt) : null;
    // DIAS DE CALENDÁRIO entre as duas datas, com os DOIS lados zerados na meia-noite local.
    // ⚠️ Antes era `floor((dataRef - ultima)/24h)` com as horas cruas, e a conta dependia da HORA em que
    // se gerava: visita às 10h + geração às 16h dava 23 dias; a mesma visita com geração às 08h dava 22.
    // Quando `normalizarDataRef` passou a zerar só o `dataRef`, o desequilíbrio virou sistemático — a
    // carteira inteira perdia um dia de atraso, e clientes na borda da janela de tolerância
    // (`diasAtraso >= -janela`) caíam da rota. Zerar os dois lados resolve e torna a conta estável.
    const diasDesde = ultima
      ? Math.round((meiaNoiteLocal(dataRef) - meiaNoiteLocal(ultima)) / 86400000)
      : null;
    const diasAtraso = diasDesde == null ? ciclo : diasDesde - ciclo; // §4: nunca visitado = vencido há 1 ciclo
    const flag = flagged.has(c.id); // "visitar depois" → força elegível e prioridade máxima na passagem 1
    cands.push({
      c, pos, estrelas, ciclo, diasDesde, diasAtraso,
      pscore: estrelas * 1000 + Math.max(0, diasAtraso) + (flag ? 100000 : 0),
      fillscore: estrelas * 1000 + diasAtraso,
      elegivel: flag || diasAtraso >= -janela,
      municipio, bairro, zona: 0,
      // Só calcula quando o rodízio está em jogo: `ehZonaRodizio` faz ponto-em-polígono por cliente, e
      // não há razão para pagar isso na carteira inteira de quem não usa a regra.
      emRodizio: diaRodizio != null && c.geoPrecision !== 'city' && ehZonaRodizio(pos, municipio),
      coordImprecisa: diaRodizio != null && c.geoPrecision === 'city' && ehMunicipioSP(municipio),
    });
  }

  // 3) zonas por k-means, ordenadas oeste→leste (longitude do centróide)
  const K = Math.min(ndays, Math.max(1, cands.length));
  if (cands.length > 0) {
    const km = kmeansZonas(cands.map((c) => c.pos), K);
    const ordem = km.centroids.map((ce, idx) => ({ idx, lng: ce.lng }))
      .sort((a, b) => a.lng - b.lng)
      .map((z, novo) => [z.idx, novo] as [number, number]);
    const mapa = new Map(ordem);
    cands.forEach((c, i) => { c.zona = mapa.get(km.labels[i]) ?? 0; });
  }

  // 3.5) RODÍZIO DE SP — REMANEJA A ZONA antes de montar os dias.
  // Índice de zona == índice de dia, e zona 0 é segunda-feira (ver DIAS_LABEL), então o dia restrito
  // é `diaRodizio - 1`. Se ele cair fora da semana planejada (ndays curto), não há o que fazer.
  const zonaRestrita = diaRodizio != null ? diaRodizio - 1 : -1;
  let trocaZona: 'nao_aplicado' | 'nao_precisou' | 'trocada' | 'sem_zona_livre' = 'nao_aplicado';
  if (zonaRestrita >= 0 && zonaRestrita < ndays) {
    // Conta RISCO, não só certeza: uma zona só serve de destino se não tiver NEM cliente confirmado
    // no Centro Expandido NEM cliente de coordenada imprecisa em São Paulo. Contar só `emRodizio`
    // deixaria a troca escolher justamente uma zona cheia de imprecisos e empurrá-los PARA DENTRO do
    // dia restrito — o motor criaria o risco sobre o qual depois avisa em "a rever".
    const riscoPorZona = new Map<number, number>();
    for (const c of cands) {
      if (c.emRodizio || c.coordImprecisa) riscoPorZona.set(c.zona, (riscoPorZona.get(c.zona) || 0) + 1);
    }
    const ceNaRestrita = cands.some((c) => c.zona === zonaRestrita && c.emRodizio);
    if (!ceNaRestrita) {
      trocaZona = 'nao_precisou';
    } else {
      // Só dias ÚTEIS servem de destino (índices 0..4 = segunda..sexta). Com `diasRota: 7` os índices
      // 5 e 6 existem, mas jogar a zona do Centro Expandido para sábado/domingo trocaria uma restrição
      // legal por uma prática: cliente fechado e vendedor de folga.
      const ultimoUtil = Math.min(ndays, 5);
      let alvo = -1;
      for (let z = 0; z < ultimoUtil; z++) {
        if (z !== zonaRestrita && (riscoPorZona.get(z) || 0) === 0) { alvo = z; break; }
      }
      if (alvo >= 0) {
        // `else if` é obrigatório: sem ele o mesmo candidato seria trocado duas vezes e voltaria ao lugar.
        for (const c of cands) {
          if (c.zona === zonaRestrita) c.zona = alvo;
          else if (c.zona === alvo) c.zona = zonaRestrita;
        }
        trocaZona = 'trocada';
      } else {
        // Vendedor que só atende o Centro Expandido: não há para onde remanejar. O bloqueio abaixo é
        // que segura, e o dia restrito fica mais vazio — a tela PRECISA dizer isso, senão o vendedor
        // lê "nada precisou sair" olhando para um dia esvaziado.
        trocaZona = 'sem_zona_livre';
      }
    }
  }

  // 3.9) REMARCAÇÕES À MÃO — sobrescrevem a zona, e é a ÚLTIMA palavra sobre o dia.
  // ⚠️ A ordem importa: tem de vir DEPOIS do swap do rodízio (3.5), que move zonas INTEIRAS. Se viesse
  // antes, o swap arrastaria o cliente remarcado junto e desfaria a escolha do vendedor em silêncio.
  // Índice fora da semana planejada é ignorado (ndays pode ter encolhido no painel desde a remarcação).
  const fixados = opts.fixados || {};
  for (const c of cands) {
    const alvo = fixados[c.c.id];
    if (typeof alvo === 'number' && Number.isInteger(alvo) && alvo >= 0 && alvo < ndays) c.zona = alvo;
  }
  const fixadoNoDia = (c: WCand, d: number) => {
    const alvo = fixados[c.c.id];
    return typeof alvo === 'number' && Number.isInteger(alvo) && alvo >= 0 && alvo < ndays && alvo === d;
  };
  const fixadoEmOutroDia = (c: WCand, d: number) => {
    const alvo = fixados[c.c.id];
    return typeof alvo === 'number' && Number.isInteger(alvo) && alvo >= 0 && alvo < ndays && alvo !== d;
  };

  // 4) montagem por dia
  const usados = new Set<string>();
  const P: DiaParams = {
    inicio: hhmmToMin(settings.jornadaInicio), fim: hhmmToMin(settings.jornadaFim),
    almIni: hhmmToMin(settings.almocoJanelaInicio), almFim: hhmmToMin(settings.almocoJanelaFim),
    almocoMin: settings.almocoMin, dur: settings.duracaoVisitaMin, minVisitas, alpha, pontoPartida, travelMin,
  };
  const dias: DiaRoteiro[] = [];
  for (let d = 0; d < ndays; d++) {
    // BLOQUEIO no dia restrito, aplicado nas DUAS listas. A segunda (`cands` inteiro) é a fonte da
    // 2ª passagem, que enche o dia puxando de OUTRAS zonas — sem bloquear ali, um cliente do Centro
    // Expandido voltaria pela porta dos fundos justamente no dia proibido. E é bloqueio ANTES de
    // montar, não filtro depois: remover parada de um dia já montado deixaria buraco no horário.
    // ⚠️ O 2º termo é o que impede a REMARCAÇÃO DE VAZAR. Sem ele o cliente fixado na sexta seria
    // consumido por um dia anterior na passagem de piso mínimo, que puxa de outras zonas com
    // `scoreFn = () => 0` — ali a prioridade não vale nada e a escolha é só por proximidade. O
    // vendedor veria o cliente cair num dia que ele não escolheu, sem nenhuma explicação.
    const bloqueado = (c: WCand) => (d === zonaRestrita && c.emRodizio) || fixadoEmOutroDia(c, d);
    dias.push(montarDia(
      d,
      cands.filter((c) => c.zona === d && !usados.has(c.c.id) && !bloqueado(c)),
      cands.filter((c) => !bloqueado(c)),
      usados, P,
      cands.filter((c) => fixadoNoDia(c, d) && !usados.has(c.c.id)),
    ));
  }

  // backlog: vencidos elegíveis que ficaram de fora
  const backlog: BacklogInfo[] = cands
    .filter((c) => c.elegivel && c.diasAtraso >= 0 && !usados.has(c.c.id))
    .sort((a, b) => b.pscore - a.pscore)
    .map((c) => ({ clientId: c.c.id, nome: c.c.name || c.c.tradeName || '', municipio: c.municipio, estrelas: c.estrelas, diasAtraso: Math.max(0, c.diasAtraso) }));

  // "Centro Expandido a rever" — DOIS motivos, com ações diferentes (ver RodizioInfo):
  //  • 'rodizio': estava na zona, vencia nesta semana e não coube em nenhum dia. A regra funcionou;
  //    o gestor decide se reagenda.
  //  • 'coordenada_imprecisa': está em São Paulo com `geoPrecision: 'city'` e CAIU no dia restrito.
  //    Não foi bloqueado de propósito — a coordenada é o centro do município e não prova que ele está
  //    na zona; tirar visita por cadastro ruim é pior que o risco. Fica o aviso: pode ser que precise
  //    de geocodificação, não de remarcação.
  const aRever: RodizioInfo[] = [];
  if (diaRodizio != null) {
    const info = (c: WCand, motivo: RodizioInfo['motivo']): RodizioInfo =>
      ({ clientId: c.c.id, nome: c.c.name || c.c.tradeName || '', municipio: c.municipio, motivo });
    // ⚠️ `c.zona === zonaRestrita` é o que torna esta lista HONESTA. Sem ele entraria todo cliente do
    // Centro Expandido que não coube na semana — inclusive os de zonas livres, que sobraram por
    // capacidade normal e apareceriam com o rodízio DESLIGADO exatamente igual. Atribuí-los ao rodízio
    // seria falso, e eles já constam do painel "Não encaixados" com o motivo verdadeiro.
    // Depois de uma troca bem-sucedida os clientes do Centro Expandido saíram da zona restrita, então
    // esta lista fica naturalmente vazia — que é o desfecho certo: foram realocados, não perdidos.
    for (const c of cands) {
      if (c.emRodizio && c.elegivel && c.zona === zonaRestrita && !usados.has(c.c.id)) aRever.push(info(c, 'rodizio'));
    }
    if (zonaRestrita >= 0 && zonaRestrita < dias.length) {
      const noDiaRestrito = new Set(dias[zonaRestrita].rota.map((s) => s.clientId));
      for (const c of cands) {
        if (c.coordImprecisa && noDiaRestrito.has(c.c.id)) aRever.push(info(c, 'coordenada_imprecisa'));
      }
    }
  }

  // REMARCADOS QUE NÃO COUBERAM. Depois da passagem zero, só sobra aqui quem não cabe nem num dia
  // vazio — na prática, cliente tão longe que a ida mais a volta já estouram a jornada. É pouco
  // provável e por isso mesmo precisa aparecer: o vendedor mandou aquele cliente para aquele dia, e
  // sumir sem explicação seria o pior desfecho possível.
  const fixadosNaoCouberam: FixadoInfo[] = [];
  for (const c of cands) {
    const alvo = fixados[c.c.id];
    if (typeof alvo !== 'number' || !Number.isInteger(alvo) || alvo < 0 || alvo >= ndays) continue;
    if (usados.has(c.c.id)) continue;
    fixadosNaoCouberam.push({ clientId: c.c.id, nome: c.c.name || c.c.tradeName || '', municipio: c.municipio, dia: alvo });
  }

  const visitas = dias.reduce((s, d) => s + d.rota.length, 0);
  return {
    data: dataRef.toISOString().slice(0, 10),
    dias, distantes, backlog, fixadosNaoCouberam,
    alertas: { semCoordenada },
    resumo: { roteirizaveis: cands.length + distantes.length, porEstrela, visitas, kmAprox: dias.reduce((s, d) => s + d.kmAprox, 0) },
    // `aplicado` é o dia restrito CAIR dentro da semana planejada — não basta ter placa. Sem isso a
    // tela não conseguiria distinguir "não tirou ninguém" de "nem chegou a rodar".
    rodizio: { aplicado: zonaRestrita >= 0 && zonaRestrita < ndays, diaRestrito: diaRodizio, troca: trocaZona, aRever },
  };
}
